import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { listJournal, deleteJournal, fetchCategories } from "@/lib/api";
import CategoryChips from "@/components/CategoryChips";
import JournalNotesTabs from "@/components/JournalNotesTabs";
import { Plus, Filter } from "lucide-react";

function relDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function JournalPage() {
    const [entries, setEntries] = useState([]);
    const [cats, setCats] = useState([]);
    const [sp, setSp] = useSearchParams();
    const catFilter = sp.get("cat");
    const subjFilter = sp.get("subject");
    const refFilter = sp.get("ref");
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);

    const load = () => {
        const params = {};
        if (catFilter && catFilter !== "__all__") params.category = catFilter;
        if (subjFilter) params.subject = subjFilter;
        if (refFilter) params.reference = refFilter;
        setLoading(true);
        listJournal(params).then(d => setEntries(d.entries || [])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [catFilter, subjFilter, refFilter]); // eslint-disable-line
    useEffect(() => { fetchCategories("journal").then(d => setCats(d.categories || [])); }, []);

    const selectCat = (id) => {
        const next = new URLSearchParams(sp);
        if (!id || id === "__all__") next.delete("cat");
        else next.set("cat", id);
        setSp(next, { replace: true });
    };
    const clearFilters = () => setSp(new URLSearchParams(), { replace: true });

    return (
        <div className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full" style={{ maxWidth: "68ch" }}>
            <JournalNotesTabs current="journal" />
            <div className="flex items-center justify-between mb-4">
                <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Journal</h1>
                <button
                    data-testid="new-journal-entry"
                    onClick={() => nav("/journal/new")}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition"
                >
                    <Plus size={16} /> New entry
                </button>
            </div>

            {(subjFilter || refFilter) && (
                <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 font-ui text-[12px] text-moss-deep mb-3"
                >
                    <Filter size={12} /> Clear filter
                </button>
            )}

            <CategoryChips
                space="journal"
                categories={cats}
                selectedId={catFilter || "__all__"}
                onSelect={selectCat}
                variant="filter"
            />

            {loading ? (
                <p className="mt-6 font-ui text-sm text-ink-40">Loading…</p>
            ) : entries.length === 0 ? (
                <div className="mt-10 text-center">
                    <p className="scripture text-[18px] text-ink-70">Write when you're ready.</p>
                    <p className="mt-1 font-ui text-[13px] text-ink-40">Your journal entries will gather here.</p>
                </div>
            ) : (
                <ul className="mt-4 space-y-3">
                    {entries.map(e => (
                        <li key={e.id}>
                            <Link
                                to={`/journal/${e.id}`}
                                data-testid={`journal-item-${e.id}`}
                                className="block p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md transition"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                                        {relDate(e.date)}
                                    </div>
                                    {e.category_id && (
                                        <CategoryBadge cats={cats} id={e.category_id} />
                                    )}
                                </div>
                                <p className="scripture text-[15px] text-ink leading-snug line-clamp-3">
                                    {e.excerpt || <span className="text-ink-40 italic">Empty entry</span>}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {(e.references || []).slice(0, 4).map(r => (
                                        <span key={r} className="px-1.5 py-0.5 rounded bg-moss/10 text-moss-deep font-ui text-[10px]">
                                            {r.replace(".", " ").replace(".", ":")}
                                        </span>
                                    ))}
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CategoryBadge({ cats, id }) {
    const c = cats.find(x => x.id === id);
    if (!c) return null;
    return (
        <span
            className="px-2 py-0.5 rounded-full font-ui text-[10px] font-medium text-white"
            style={{ background: c.color }}
        >
            {c.name}
        </span>
    );
}
